import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { ArrowRight } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO
        title="Field Notes — Leadership Essays"
        description="Short essays on leadership from twenty years on the fireground — written for leaders who'd rather think clearly than scroll endlessly."
      />
      <section className="border-b border-brand-line">
        <div className="container-narrow py-20 md:py-28">
          <span className="overline text-brand-rust" data-testid="blog-overline">Field Notes</span>
          <h1 className="font-display text-5xl md:text-6xl mt-4 text-brand-ink leading-[1.05]">Field Notes</h1>
          <p className="mt-6 text-brand-inkMuted max-w-2xl leading-relaxed">
            Short essays on leadership — drawn from twenty years on the fireground and the lessons in <em>Forged in Fire</em>. Written for leaders who'd rather think clearly than scroll endlessly.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16">
        {loading ? (
          <div className="overline text-brand-inkMuted text-center py-20">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="border border-brand-line bg-white p-16 text-center" data-testid="blog-empty">
            <p className="font-serif text-2xl text-brand-ink">No posts yet.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <Link
                key={p.slug}
                to={`/field-notes/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white border border-brand-line hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex flex-col"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden bg-brand-bg">
                  <img
                    src={p.cover_image}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ objectPosition: (p.cover_image || "").includes("forged%20in%20fire") ? "right center" : "center" }}
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  {p.tag && <span className="overline text-brand-rust">{p.tag}</span>}
                  <h3 className="font-serif text-xl text-brand-ink mt-3 leading-snug">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-inkMuted leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <span className="mt-auto pt-5 overline text-brand-rust group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                    Read essay <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
